import {
    AfterViewInit,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import Swal from 'sweetalert2';
import { MatDialog } from '@angular/material/dialog';
import { DashboardService } from './dashboard.service';
import { DocumentInternComponent } from './doc-intern/document-intern.component';
import { DataTableDirective } from 'angular-datatables';
import {
    Observable,
    Subject,
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    of,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { Generic, GenericCompartimentSearch } from '../../interfaces/generic.interface';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { DocumentIesireComponent } from './doc-iesire/document-iesire.component';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { FormControl, FormGroup } from '@angular/forms';
import { InfoDocumentComponent } from './info-doc/info-document.component';
import { CommonService } from '../../services/common.service';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { DocIntrareComponent } from './doc-intrare/doc-intrare.component';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GeneralData, DocumentListDT, StoreDocResponse } from './document.interface';
import { GeneralService } from 'src/app/platform/nomenclatoare/general/general.service';
import { ToastService } from 'src/app/services/toast.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Location } from '@angular/common';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(20px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(20px)' }))
            ])
        ])
    ]
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;
    isLoading = false;
    isFilterLoading = false;
    isGeneralDataLoading = false;
    generalData: GeneralData[] = [];

    private dialogRef: any = null;
    private historyStateAdded = false;

    appName = environment.config.appName;
    appNameSmall = environment.config.appNameSmall;
    appDescription = environment.config.appDescription;
    dgaspcId = environment.interop.dgaspcId;
    documentId: number | null = null;

    showCard = false;
    showDirectieCompartiment = true;

    selectedFiltre: 'simple' | 'avansate' = 'simple';

    // Scroll to top
    showScrollToTop = false;
    private scrollThreshold = 300; // Show button after scrolling 300px

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        this.showScrollToTop = window.scrollY > this.scrollThreshold;
    }

    scrollToTop(): void {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    // Popover dates
    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    // tomorrow: Date = new Date();
    // selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    selectedStartDate: Date = new Date(new Date().getFullYear() - 1, 0, 1);
    private _selectedEndDate: Date = new Date();
    // selectedFilteringButton: string = 'current_year';
    selectedFilteringButton: string = 'previous_year';

    // Last day of the current year
    lastDayOfYear: Date = new Date(new Date().getFullYear(), 11, 31);
    lastDayOfNextYear: Date = new Date(new Date().getFullYear() + 1, 11, 31);

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);
        this.selectedEndDate = this.today;
        this.updateCalendarView(this.endCalendar, this.today);
        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setToday(): void {
        this.selectedStartDate = this.today;
        this.updateCalendarView(this.startCalendar, this.today);
        this.selectedEndDate = this.today;
        this.updateCalendarView(this.endCalendar, this.today);
        this.rerenderDT();
        this.selectedFilteringButton = `today`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    calendarYears: number[] = [2019, 2020, 2021, 2022, 2023, 2024, 2025]

    setSpecificYear(year: number): void {
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = new Date(year, 11, 31); // 31 Decembrie
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton = year.toString();
    }

    setAllYears(): void {
        this.selectedStartDate = new Date(this.calendarYears[0], 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = new Date(this.calendarYears[this.calendarYears.length - 1], 11, 31); // 31 Decembrie
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton = 'all_years';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    // Date popover filtering

    dateFilteringOptions: { id: number; value: string }[] = [
        { id: 1, value: 'Adăugare' },
        { id: 2, value: 'Ultima actualizare' },
        { id: 3, value: 'Finalizare' },
        { id: 4, value: 'Invalidare' },
    ];

    dateFilterOption: number = 1;

    onDateFilterChange(): void {
        this.rerenderDT();
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink =
        environment.interop.basePath +
        environment.interop.indoc.documents.getDocumentsListDT;

    parameters: { key: string; value: number }[] = [
        { key: '--Alegeți--', value: -1 },
        { key: 'Da', value: 1 },
        { key: 'Nu', value: 0 },
    ];

    provenientaDocumente: { key: string; value: number }[] = [
        { key: 'Toate', value: -1 },
        { key: 'inDoc', value: 1 },
        { key: 'eConfirmare', value: 2 },
        { key: 'Arhivă', value: 3 },
    ];

    toggleCard() {
        this.showCard = !this.showCard;
    }

    // Values for status filtering
    defaultParameter: { name: string; id: number } = {
        name: 'Toate',
        id: -1,
    };

    selectedStatusID: number = this.defaultParameter.id;
    statusParametersValue!: string;
    notificationParameter: { id: string; name: string } = { id: '-1', name: '' };

    notificationServiceBefore: any = {
        currentStatus: -1,
        currentCategory: 6,
    }

    // Values for category filtering
    categoryToggleOptions = [
        { id: 3, value: 'Toate' },
        { id: 6, value: 'Vizualizare toate documentele' },
        { id: 7, value: 'Vizualizare doc institutie' },
        { id: 2, value: 'Departamentul meu' },
        { id: 1, value: 'Documentele mele' },
        { id: 5, value: 'De finalizat' },
        { id: 4, value: 'Mesaje interne' },
        { id: 9, value: 'De semnat' },
    ];

    selectedCategory: number = this.categoryToggleOptions[1].id;
    categoryParametersValue!: string;

    myDepartmentSubsectionOptions = [
        { id: -1, name: "-- Alegeți --" },
        { id: 3, name: "Origine serviciu/compartiment propriu" },
        { id: 4, name: "Origine personală" }
    ];
    myDocumentSubsectionOptions = [
        { id: -1, name: "-- Alegeți --" },
        { id: 1, name: "De preluat pe serviciu/compartiment" },
        { id: 2, name: "De preluat personal (operator selectat)" },
        { id: 3, name: "Preluate pe serviciu/compartiment" },
        { id: 4, name: "Preluate personal (operator selectat)" }
    ];
    toFinishSubsectionOptions = [
        { id: -1, name: "-- Alegeți --" },
        { id: 1, name: "De finalizat de serviciu/compartiment" },
        { id: 2, name: "De finalizat personal" }
    ];

    myMessagesSubsectionOptions = [
        { id: -1, name: "-- Toate --" },
        { id: 1, name: "Necitite" }
    ];

    myCheckSignDocsSubsectionOptions = [
        { id: 1, name: "De semnat" },
        { id: 2, name: "Semnate" }
    ];

    selectedDocumentSubsection = -1;
    selectedDepartmentSubsection = -1;
    selectedFinishSubsection = -1;
    selectedMessageSubsection = -1;
    selectedToSignSubsection = 1

    private destroy$ = new Subject<void>();

    registreActiveSwitch: Generic[] = [];
    selectedRegistreActiveSwitch: number = -1;

    statusuri: Generic[] = [];
    serii: Generic[] = [];
    tipuriFinalizare: Generic[] = [];
    surse: Generic[] = [];
    tipuriAcces: Generic[] = [];
    tipuriEntitati: Generic[] = [];

    directii: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimente: GenericCompartimentSearch[] = [];
    compartimenteControl = new FormControl();
    isCompartimenteLoading = false;
    showCompartimenteMessage = true;
    compartimenteLoadComplete = false;

    operatori: Generic[] = [];
    filteredOperatori: Generic[] = [];
    operatoriControl = new FormControl();

    tipDocumente: Generic[] = [];
    filteredTipDocumente: any[] = [];
    tipDocumenteControl = new FormControl();

    activeFilters = 0;
    searchParamsForm!: FormGroup;

    //Form Initial Values
    initialFormValues = {
        id_serie: -1,
        reg_number: '', // Numar document
        extern_numar: '',
        id_categorie: -1, // Tip access
        id_address_type: -1, // Tip emitent
        denumire_entity: '',
        cnp_entity: '',
        email: '',
        address_search_query_obs: '',  // Observatii
        keywords: '',
        id_directie: null,
        id_compartiment: null,
        id_user: -1,
        id_tip_document: -1,
        finalizare_type: -1,
        source: -1, // Cale intrare
        id_document: null,
        source_object: -1, // Provenienta document
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            id_serie: new FormControl(-1, []),
            reg_number: new FormControl('', []),
            extern_numar: new FormControl('', []),
            id_categorie: new FormControl(-1, []),
            id_address_type: new FormControl(-1, []),
            denumire_entity: new FormControl('', []),
            cnp_entity: new FormControl('', []),
            email: new FormControl('', []),
            address_search_query_obs: new FormControl('', []),
            keywords: new FormControl('', []),
            id_directie: new FormControl(null, []),
            id_compartiment: new FormControl(null, []),
            id_user: new FormControl(-1, []),
            id_tip_document: new FormControl(-1, []),
            finalizare_type: new FormControl(-1, []),
            source: new FormControl(-1, []),
            id_document: new FormControl(this.documentId, []),
            source_object: new FormControl(-1, []),
        });
    }

    checkActiveFilters() {
        this.activeFilters = 0;
        const currentFilters = this.searchParamsForm.value;

        for (const [key, value] of Object.entries(this.initialFormValues)) {
            if (key in currentFilters && currentFilters[key] !== value) {
                this.activeFilters++
            }
        }
    }

    resetForm() {
        this.resetFieldsSearch();

        this.searchParamsForm.reset(this.initialFormValues);
        this.activeFilters = 0;

        // If there is a query id in the url, remove it when changing filters 
        this.removeQueryParam('id')
        this.rerenderDT();
        this.showCard = false;
    }

    resetFieldsSearch() {
        this.filteredDirectii = this.directii;
        this.directiiControl.setValue('');

        this.compartimente = [];
        this.compartimenteControl.setValue('');

        this.operatori = [];
        this.filteredOperatori = [];
        this.operatoriControl.setValue('');

        this.filteredTipDocumente = this.tipDocumente;
        this.tipDocumenteControl.setValue('');

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch()
    }

    applyFilters(): void {
        this.showCard = false;
        // If there is a query id in the url, remove it when changing filters 
        this.removeQueryParam('id');
        this.rerenderDT(true);
        this.checkActiveFilters();
    }

    removeQueryParam(paramName: string) {
        this.router.navigate([], {
            queryParams: { [paramName]: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private dialog: MatDialog,
        public dashboardService: DashboardService,
        private commonService: CommonService,
        private generalService: GeneralService,
        public permissionsService: PermissionsService,
        private notificationService: NotificationService,
        private route: ActivatedRoute,
        private router: Router,
        private toastService: ToastService,
        private location: Location
    ) { }

    ngOnInit() {
        const documentId = this.route.snapshot.queryParamMap.get('id');
        this.documentId = documentId ? Number(documentId) : null;

        console.log(this.categoryToggleOptions, 'is it here?')

        this.renderDT();
        this.buildSearchParamsForm();
        this.checkActiveFilters();

        this.filterCategoryOptions();
        this.filterSubsectionsOptions();
        setTimeout(() => this.redirectFromNotifications(), 0);

        this.loadActiveSwitch();
        this.loadStatusesActive();
        this.loadSeriiActive();
        this.loadTipuriFinalizareActive();
        this.loadSurseActive();
        this.loadTipuriAccesActive();
        this.loadTipuriEntitatiActive();
        this.loadDirectiiActive();
        this.loadTipDocumenteActive();

        this.loadGeneralData();

        this.compartimenteControl.valueChanges.pipe(
            debounceTime(600)
        ).subscribe(compartiment => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimente(compartiment);
                this.showCompartimenteMessage = true;
                this.compartimenteLoadComplete = false;
            }
        });

        this.renderFieldsSearch();

        window.addEventListener('popstate', this.handlePopState);
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();

        window.removeEventListener('popstate', this.handlePopState);

        // Reset notification in the BehaviorSubject
        this.notificationService.changeNotification({ id: '-1', name: 'none' });
    }

    // Handler pentru tasta ESC
    @HostListener('document:keydown.escape', ['$event'])
    handleEscapeKey(event: Event) {
        if (this.dialogRef) {
            event.preventDefault();
            this.dialogRef.close();
        }
    }

    // Handler pentru butonul back
    private handlePopState = (event: PopStateEvent) => {
        if (this.dialogRef && this.historyStateAdded) {
            // event.preventDefault();
            this.dialogRef.close();
            this.historyStateAdded = false;
        }
    };

    redirectFromNotifications() {
        this.notificationService.currentNotification.subscribe(
            (notification: { id: string; name: string }) => {
                if (notification && notification.id !== '-1') {
                    // here we need to update our buttons
                    this.notificationServiceBefore.currentStatus = this.selectedStatusID;
                    this.notificationServiceBefore.currentCategory = this.selectedCategory;

                    this.selectedStatusID = -1;
                    if (notification.id === '1' && this.categoryToggleOptions.some(option => option.id === 1)) {
                        this.selectedCategory = 1; // Documentele mele
                        this.selectedDocumentSubsection = 2; // De preluat personal 
                        this.notificationParameter = notification; // Update chip
                    } else if (notification.id === '2' && this.categoryToggleOptions.some(option => option.id === 4)) {
                        this.selectedCategory = 4; // Mesaje interne
                        this.selectedMessageSubsection = 1; // Necitite 
                        this.notificationParameter = notification; // Update chip
                    }

                    this.rerenderDT();
                }
            }
        );
    }

    removeChip(): void {
        // Resets the notification parameter which we send to our backend with -1 so the filter for this particular flag is canceled
        this.notificationParameter = { id: '-1', name: '' };

        // here we need to update our buttons
        this.selectedStatusID = this.notificationServiceBefore.currentStatus;
        this.selectedCategory = this.notificationServiceBefore.currentCategory;

        this.rerenderDT();
    }


    loadActiveSwitch() {
        this.isLoading = true;
        this.commonService
            .getActiveSwitch()
            .pipe(
                map((resp: Generic[]) => [{ name: '--Serie--', id: -1 }, ...resp]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.registreActiveSwitch = results;
                    if (this.registreActiveSwitch.length > 1) {
                        this.selectedRegistreActiveSwitch = this.registreActiveSwitch[1].id;
                    }
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                },
            });
    }

    loadStatusesActive() {
        this.isFilterLoading = true;
        this.commonService
            .getStatusesActive()
            .pipe(
                takeUntil(this.destroy$),
                map((results: Generic[]) => [...results, this.defaultParameter]),
                finalize(() => (this.isFilterLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.statusuri = results;
                },
                error: (error) => {
                    console.error('Error loading request statuses', error);
                    this.isFilterLoading = false;
                },
            });
    }

    loadSeriiActive() {
        this.isFilterLoading = true;
        this.commonService
            .getSeriiActive()
            .pipe(
                takeUntil(this.destroy$),
                map((resp: Generic[]) => [{ name: '--Serie--', id: -1 }, ...resp]),
                finalize(() => (this.isFilterLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.serii = results;
                },
                error: (error) => {
                    console.error('Error loading serii', error);
                    this.isFilterLoading = false;
                },
            });
    }

    loadTipuriFinalizareActive() {
        this.isLoading = true;
        this.commonService
            .getTipuriFinalizareActive()
            .pipe(
                takeUntil(this.destroy$),
                map((resp: Generic[]) => [this.defaultParameter, ...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tipuriFinalizare = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri acces', error);
                    this.isLoading = false;
                },
            });
    }

    loadSurseActive() {
        this.isLoading = true;
        this.commonService
            .getSurseActive()
            .pipe(
                takeUntil(this.destroy$),
                map((resp: Generic[]) => [this.defaultParameter, ...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.surse = results;
                },
                error: (error) => {
                    console.error('Error loading surse', error);
                    this.isLoading = false;
                },
            });
    }

    loadTipuriAccesActive() {
        this.isLoading = true;
        this.commonService
            .getTipuriAccesActive()
            .pipe(
                takeUntil(this.destroy$),
                map((resp: Generic[]) => [this.defaultParameter, ...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tipuriAcces = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri acces', error);
                    this.isLoading = false;
                },
            });
    }

    loadTipuriEntitatiActive() {
        this.isLoading = true;
        this.commonService
            .getTipuriEntitatiActive()
            .pipe(
                takeUntil(this.destroy$),
                map((resp: Generic[]) => [this.defaultParameter, ...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tipuriEntitati = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri entitati', error);
                    this.isLoading = false;
                },
            });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.directii);
                }
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directii;
                    return of(this.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.operatori);
                }
                if (inputValue.length < 1) {
                    this.filteredOperatori = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatori = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tipDocumenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.tipDocumente);
                }
                if (inputValue.length < 1) {
                    this.filteredTipDocumente = this.tipDocumente;
                    return of(this.tipDocumente);
                }
                return this.filterTipDocumente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTipDocumente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive() {
        this.isFilterLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directii = results;

                    this.filteredDirectii = [...this.directii];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    onDirectiiChange(selectedCompartiment: Generic): void {
        this.searchParamsForm.controls['id_directie'].patchValue(selectedCompartiment.id);

        this.compartimenteControl.patchValue('');
        this.searchParamsForm.controls['id_compartiment'].patchValue(null);

        this.operatori = [];
        this.filteredOperatori = [];
        this.operatoriControl.patchValue('');
        this.searchParamsForm.controls['id_user'].patchValue(-1);
    }

    displayDirectii(selectedDirectie: Generic): string {
        return selectedDirectie && selectedDirectie.name ? selectedDirectie.name : '';
    }

    onDirectiiBlur() {
        const currentValue = this.directiiControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.directiiControl.patchValue('');
            this.searchParamsForm.controls['id_directie'].patchValue(null);

            this.compartimenteControl.patchValue('');
            this.searchParamsForm.controls['id_compartiment'].patchValue(null);

            this.operatori = [];
            this.filteredOperatori = [];
            this.operatoriControl.patchValue('');
            this.searchParamsForm.controls['id_user'].patchValue(-1);
        }
    }

    fetchCompartimente(compartiment: string) {
        // const directieValue = this.searchParamsForm.get('id_directie')?.value;
        // const directie = directieValue && directieValue !== -1 ? directieValue : undefined;
        if (compartiment.length >= 2) {
            this.isCompartimenteLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: any) => {
                    this.compartimente = response;
                    this.isCompartimenteLoading = false;
                    this.compartimenteLoadComplete = true;
                    this.showCompartimenteMessage = this.compartimente.length === 0;
                },
                error: (err: any) => {
                    this.isCompartimenteLoading = false;
                    this.showCompartimenteMessage = true;
                    this.compartimenteLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.compartimente = [];
            this.showCompartimenteMessage = true;
            this.compartimenteLoadComplete = false;
        }
    }

    onCompartimenteSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.searchParamsForm.controls['id_compartiment'].patchValue(selectedCompartiment.id);

        this.directiiControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.searchParamsForm.controls['id_directie'].patchValue(selectedCompartiment.directie_id);

        this.loadOperatori();
        this.operatoriControl.patchValue('');
        this.searchParamsForm.controls['id_user'].patchValue(-1);
    }

    displayCompartimente(selectedCompartiment: GenericCompartimentSearch): string {
        return selectedCompartiment && selectedCompartiment.name ? selectedCompartiment.name : '';
    }

    onCompartimenteBlur() {
        const currentValue = this.compartimenteControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.compartimenteControl.patchValue('');
            this.searchParamsForm.controls['id_compartiment'].patchValue(null);

            this.operatori = [];
            this.filteredOperatori = [];
            this.operatoriControl.patchValue('');
            this.searchParamsForm.controls['id_user'].patchValue(-1);
        }
    }

    loadOperatori() {
        this.isFilterLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.searchParamsForm.controls['id_directie'].value,
            compartiment_id: this.searchParamsForm.controls['id_compartiment'].value
        }
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isFilterLoading = false })
            ).subscribe({
                next: (data) => {
                    this.operatori = [...data];
                    this.filteredOperatori = [...this.operatori];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredOperatori = [];
                },
            });
    }

    // onOperatoriClosed() {
    //     const selectedValue = this.operatoriControl.value;

    //     if (selectedValue) {
    //         const isSelectedInFilteredList = this.filteredOperatori.some(operator =>
    //             operator.id === this.searchParamsForm.get('id_user')?.value
    //         );

    //         if (!isSelectedInFilteredList) {
    //             this.operatoriControl.setValue(null);
    //             this.filteredOperatori = this.operatori;
    //             this.destroy$.next();
    //             this.destroy$.complete();
    //             this.renderFieldsSearch();
    //         }
    //     }
    // }

    onOperatoriChange(operatorId: number): void {
        // const selectedOperator = this.operatori.find(operator => operator.id === operatorId);

        // if (selectedOperator) {
        //     this.searchParamsForm.controls['id_user'].patchValue(selectedOperator.id);
        // }
    }

    // displayOperatori(selectedOperator: Generic): string {
    //     return selectedOperator && selectedOperator.name ? selectedOperator.name : '';
    // }

    // onOperatoriBlur() {
    //     const currentValue = this.operatoriControl.value;

    //     // If the value is a string (not an object), it means user didn't select from list
    //     if (typeof currentValue === 'string') {
    //         this.operatoriControl.patchValue('');
    //         this.searchParamsForm.controls['id_user'].patchValue(-1);
    //     }
    // }

    loadTipDocumenteActive() {
        this.isFilterLoading = true;
        this.commonService
            .getDocumenteActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isFilterLoading = false })
            ).subscribe({
                next: (data) => {
                    this.tipDocumente = [...data];
                    this.filteredTipDocumente = [...this.tipDocumente];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredTipDocumente = [];
                },
            });
    }

    onTipDocumenteClosed() {
        const selectedValue = this.tipDocumenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTipDocumente.some(doc =>
                doc.id === this.searchParamsForm.get('id_tip_document')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tipDocumenteControl.setValue(null);
                this.filteredTipDocumente = this.tipDocumente;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadGeneralData(): void {
        this.isGeneralDataLoading = true;
        this.generalService
            .list()
            .pipe(
                finalize(() => { this.isGeneralDataLoading = false })
            ).subscribe({
                next: (response: any) => {
                    this.generalData = response.data;
                },
                error: (error) => {
                    console.error('Error loading data:', error);
                    this.isGeneralDataLoading = false;
                }
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatori.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterTipDocumente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tipDocumente.filter(doc =>
            this.normalizeString(doc.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    renderDT() {
        this.dtOptions = {
            order: [[0, 'desc']],
            responsive: true,
            serverSide: true,
            searching: false,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                dataTablesParameters.id_status = this.selectedStatusID;
                dataTablesParameters.id_choice = this.selectedCategory;
                dataTablesParameters.tip_date = +this.dateFilterOption;
                dataTablesParameters.date_from = this.formatDate(
                    this.selectedStartDate
                );
                dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);
                // dataTablesParameters.notificationParameter = this.notificationParameter.id;

                dataTablesParameters.department_sub_section = this.selectedDepartmentSubsection;
                dataTablesParameters.my_document_sub_section = this.selectedDocumentSubsection;
                dataTablesParameters.finish_sub_section = this.selectedFinishSubsection;
                dataTablesParameters.internal_messages_status = this.selectedMessageSubsection;
                dataTablesParameters.to_sign_section = this.selectedToSignSubsection;

                Object.assign(dataTablesParameters, this.searchParamsForm.value)
                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });

                        if (resp.data.recordsFiltered < dataTablesParameters.start) {
                            this.rerenderDT(true);  // sets dataTablesParameters.start = 0, goes to page 1
                        }
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: '#',
                    data: 'identificationColumn',
                    // render: (data: any, type: any, row: any) => {
                    //     return `
                    // 		${row.id_document} / ${row.created_at}
                    // 	`;
                    // },
                },
                {
                    title: 'Tip Document',
                    data: 'subject',
                    sortable: false,
                    // render: (data: any, type: any, row: any) => {
                    //     return `
                    // 		<span><strong>${row.subject}</strong></span><br/><br/>

                    // 	`;
                    // },
                },
                {
                    title: 'Origine Document',
                    data: 'documentSource',
                    sortable: false,
                },
                {
                    title: 'Emitent',
                    data: 'entityData',
                    sortable: false,
                },
                {
                    title: 'Status',
                    data: 'status',
                    sortable: false,
                },
                {
                    title: 'Destinatari',
                    data: 'destinatari',
                    sortable: false,
                },
                // {
                //     title: 'Înregistrat',
                //     data: 'created',
                //     sortable: true,
                // },
                // {
                //     title: 'Prioritate',
                //     data: 'priority',
                //     sortable: false,
                // },
                {
                    title: 'Prioritate',
                    data: 'sla',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `
							<span style="border-radius:10px; padding:4px 8px; white-space: nowrap; color: ${row.priority_color}; background: ${row.priority_bg_color}">${row.priority_name}</span><br/><br/>
							<span style="color:#bdbdbd; font-size:12px">SLA</span><br/>
							<span>${row.sla}</span><br/>
						`;
                    },
                },
                {
                    title: 'Soluționare',
                    data: 'solutionare',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        // Enhanced display with multiple functions and active status
                        let html = '';

                        if (row.documente_generate && row.documente_generate.length > 0) {
                            row.documente_generate.forEach((doc: any) => {
                                if (doc.signatures && doc.signatures.length > 0) {
                                    html += '<div class="mb-2">';
                                    html += `<strong>${doc.template_name || 'Document'}</strong><br/>`;

                                    doc.signatures.forEach((sig: any) => {
                                        html += '<span class="d-inline-block me-2 mb-1">';

                                        // Function name with active indicator
                                        if (sig.use_active_function) {
                                            html += '<i class="fa-solid fa-star text-primary" title="Funcție activă"></i> ';
                                        }

                                        html += `<span class="badge badge-secondary">${sig.user_type_name || sig.functie_name}</span>`;

                                        // Show specific person if assigned
                                        if (sig.specific_user_name) {
                                            html += ` <small class="text-muted">(${sig.specific_user_name})</small>`;
                                        }

                                        // Show status
                                        if (sig.action_date) {
                                            html += ' <i class="fa-solid fa-check text-success"></i>';
                                        } else if (sig.specific_user_id) {
                                            html += ' <i class="fa-solid fa-user-check text-info"></i>';
                                        } else {
                                            html += ' <i class="fa-solid fa-clock text-warning"></i>';
                                        }

                                        html += '</span>';
                                    });

                                    html += '</div>';
                                }
                            });
                        }

                        // Fallback to original data if no enhanced info
                        if (!html && data) {
                            html = `${data}`;
                        }

                        return html || 'N/A';
                    },
                },
                // {
                //     title: 'SLA',
                //     data: 'sla',
                //     sortable: false,
                // },
                {
                    title: 'Actualizat',
                    data: 'updated',
                    sortable: true,
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');

                $('td:not(:first-child)', row).on('click', () => {
                    // this.onRowClick(data);
                });
                $('td:not(:first-child)', row).on('click', () => {
                    this.onRowClick(data);
                });
                return row;
            },
        };
    }

    rerenderDT(paginate = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
    }

    openAddDialogIntrare() {
        const dialogRef = this.dialog.open(DocIntrareComponent, {
            width: '1000px',
            maxWidth: '90vw',
            minHeight: '200px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: {
                generalData: this.generalData,
                serieId: this.selectedRegistreActiveSwitch,
            }
        });
        dialogRef.afterClosed().subscribe(({ success, resData }: { success: boolean, resData: StoreDocResponse }) => {
            this.rerenderDT();  // Allways rerender listDT
            if (success) {
                this.toastService.openToast({
                    title: this.helper.dialogConfig.headers.success,
                    message: `Documentul înregistrat cu succes cu numărul ${resData.reg_number} - ${resData.reg_date}`,
                    type: this.helper.dialogConfig.icons.success,
                });
            }
        });
    }

    openAddDialogIntern() {
        const dialogRef = this.dialog.open(DocumentInternComponent, {
            width: '1000px',
            maxWidth: '90vw',
            minHeight: '200px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: {
                generalData: this.generalData,
                serieId: this.selectedRegistreActiveSwitch,
            }
        });
        dialogRef.afterClosed().subscribe(({ success, resData }: { success: boolean, resData: StoreDocResponse }) => {
            this.rerenderDT();  // Allways rerender listDT
            if (success) {
                this.toastService.openToast({
                    title: this.helper.dialogConfig.headers.success,
                    message: `Documentul înregistrat cu succes cu numărul ${resData.reg_number} - ${resData.reg_date}`,
                    type: this.helper.dialogConfig.icons.success,
                });
            }
        });
    }

    openAddDialogIesire() {
        const dialogRef = this.dialog.open(DocumentIesireComponent, {
            width: '1000px',
            maxWidth: '90vw',
            minHeight: '200px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: {
                generalData: this.generalData,
                serieId: this.selectedRegistreActiveSwitch,
            }
        });
        dialogRef.afterClosed().subscribe(({ success, resData }: { success: boolean, resData: StoreDocResponse }) => {
            this.rerenderDT();  // Allways rerender listDT
            if (success) {
                this.toastService.openToast({
                    title: this.helper.dialogConfig.headers.success,
                    message: `Documentul înregistrat cu succes cu numărul ${resData.reg_number} - ${resData.reg_date}`,
                    type: this.helper.dialogConfig.icons.success,
                });
            }
        });
    }

    openDocument() {
        this.dashboardService.getManual().subscribe((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        });
    }

    onStatusChange(event: MatButtonToggleChange) {
        this.isLoading = true;
        this.selectedStatusID = event.value;

        this.statusParametersValue = event.value === 'toate' ? '-1' : '1';
        this.rerenderDT();
    }

    onCategoryChange(event: MatButtonToggleChange) {
        // this.isLoading = true; // Assuming you have an isLoading flag for loading state
        this.selectedCategory = event.value;

        const selectedOption = this.categoryToggleOptions.find(option => option.id === event.value);
        this.categoryParametersValue = selectedOption ? selectedOption.value : 'default';

        // Reset directie compartiment
        this.filteredDirectii = this.directii;
        this.directiiControl.setValue('');
        this.searchParamsForm.controls['id_directie'].patchValue(null);

        this.compartimente = [];
        this.compartimenteControl.setValue('');
        this.searchParamsForm.controls['id_compartiment'].patchValue(null);

        this.operatori = [];
        this.filteredOperatori = [];
        this.operatoriControl.setValue('');
        this.searchParamsForm.controls['id_user'].patchValue(-1);

        if (this.selectedCategory === 3 || this.selectedCategory === 6) {
            this.showDirectieCompartiment = true;
        } else {
            this.showDirectieCompartiment = false;
        }

        // Reset category subsections
        this.selectedDepartmentSubsection = -1;
        this.selectedDocumentSubsection = -1;
        this.selectedFinishSubsection = -1;
        this.selectedMessageSubsection = -1;
        this.selectedToSignSubsection = 1

        // Reset chip
        this.notificationParameter = { id: '-1', name: '' };

        this.rerenderDT();
    }

    filterCategoryOptions() {
        const permissionMapping: { [key: number]: string } = {
            3: "indoc-module.registratura_documents.toate",  // Toate
            6: "indoc-module.registratura_documents.list-all",  // Vizualizare toate documentele
            7: "indoc-module.registratura_documents.list-all-superadmin",  // Vizualizare doc institutie
            2: "indoc-module.registratura_documents.list-my-departments",  // Departamentul meu
            1: "indoc-module.registratura_documents.list-my-requests",  // Documentele mele
            5: "indoc-module.registratura_documents.to-close",  // De finalizat
            4: "indoc-module.registratura_documents.list-intern-messages",  // Mesaje interne
            9: "indoc-module.registratura_documents.to-sign",  // De semnat
        };

        // Filter categoryToggleOptions based on permissions
        this.categoryToggleOptions = this.categoryToggleOptions.filter(option =>
            this.permissionsService.hasPermission(permissionMapping[option.id])
        );

        // Initialize selectedCategory after filtering
        if (this.categoryToggleOptions.some(option => option.id === 6)) {
            this.selectedCategory = 6;
        } else {
            this.selectedCategory = this.categoryToggleOptions[0]?.id || -1;
        }
    }

    filterSubsectionsOptions() {
        // Filter myDocumentSubsectionOptions based on permissions
        if (!this.permissionsService.hasPermission("indoc-module.registratura_documents.operations-responsible-operators-in-charge.my_documents_see_waiting_documents_department")) {
            const indexToRemove = this.myDocumentSubsectionOptions.findIndex(option => option.id === 1)
            this.myDocumentSubsectionOptions.splice(indexToRemove, 1)
        }
        if (!this.permissionsService.hasPermission("indoc-module.registratura_documents.operations-responsible-operators-in-charge.my_documents_working_documents_department")) {
            const indexToRemove = this.myDocumentSubsectionOptions.findIndex(option => option.id === 3)
            this.myDocumentSubsectionOptions.splice(indexToRemove, 1)
        }
        // Filter myDepartmentSubsectionOptions based on permissions
        if (!this.permissionsService.hasPermission("indoc-module.registratura_documents.operations-responsible-operators-in-charge.my_department_origin_documents_loaded")) {
            const indexToRemove = this.myDepartmentSubsectionOptions.findIndex(option => option.id === 3)
            this.myDepartmentSubsectionOptions.splice(indexToRemove, 1)
        }
        // Filter toFinishSubsectionOptions based on permissions
        if (!this.permissionsService.hasPermission("indoc-module.registratura_documents.operations-responsible-operators-in-charge.to_finish_my_department_documents")) {
            const indexToRemove = this.toFinishSubsectionOptions.findIndex(option => option.id === 1)
            this.toFinishSubsectionOptions.splice(indexToRemove, 1)
        }
    }

    onRowClick(rowData: DocumentListDT) {
        this.openInfoDialog(rowData);
    }

    openInfoDialog(docDataListDT: DocumentListDT) {
        // Adaugă un state în history pentru a intercepta back button
        history.pushState({ modal: true }, '', window.location.href);
        this.historyStateAdded = true;

        this.dialogRef = this.dialog.open(InfoDocumentComponent, {
            minHeight: '100vh',
            maxHeight: '100vh',
            minWidth: '100vw',
            maxWidth: '100vw',
            disableClose: true,
            data: {
                docDataListDT,
                generalData: this.generalData,
                directii: this.directii
            },
        });

        this.dialogRef.afterClosed().subscribe(() => {
            // Curăță history state dacă dialogul se închide prin alte metode
            if (this.historyStateAdded) {
                this.historyStateAdded = false;
                // Elimină state-ul din history fără să navigheze
                history.replaceState(null, '', window.location.href);
            }
            this.dialogRef = null;
            this.rerenderDT();
        });
    }

    onFiltreChange($event: Event, type: 'simple' | 'avansate') {
        $event.preventDefault();
        this.selectedFiltre = type;
    }
}
